/* istanbul ignore file */

// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { PendingApplyProgram, StateTree } from '@makemydeal/dr-dash-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { DashMiddleware, DashStore, DashNext } from '@makemydeal/dr-shared-store';

// actions
import {
    applySelectedProgram,
    clearProgramsForVehicleChange,
    clearRetainedProgramDisplay,
    clearPendingApplyProgram,
    clearProgramLossIncentives,
    disableProgramLossIncentives,
    pruneProgramLossIncentives,
    reapplyKeptLenderProgram,
    setAvailableIncentivesFromPayment,
    settleKeptLenderLookup,
    startKeptLenderLookup,
    setRetainedProgramDisplay
} from '../actions/programActionCreators';
import { VEHICLE_CHANGE_SUCCESS } from '@makemydeal/dr-activities-change-vehicle';
import { INIT_SUCCESS } from '@makemydeal/dr-shared-store/actionTypes/initActionTypes';
import {
    selectedConditionalIncentives,
    updateCashAvailableIncentives,
    updateCurrentCalculationEndpoint,
    updatePaymentGridToggle,
    updateTermAvailableIncentives
} from '../actions/offerRedux';
import { CALC_ENDPOINTS, CDL_USER_INTENT } from '../constants';
import { KEEP_LENDER_META } from '../programInvalidatingActions';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { PAYMENT_FAILURE, PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';
import {
    PROGRAMS_INCENTIVES_REQUEST_FAILURE,
    PROGRAMS_INCENTIVES_REQUEST_SUCCESS,
    REQUEST_PROGRAMS_FAILURE,
    REQUEST_PROGRAMS_SUCCESS
} from '../actionTypes/offerActionTypes';
import type { Incentive, ProgramIncentives, ProgramIncentivesMeta, ProgramQuote } from '@makemydeal/dr-dash-types';
import { CASH } from '@makemydeal/dr-platform-types';
import type { GenericObject, OfferType } from '@makemydeal/dr-platform-types';
import { actions as offerReduxActionTypes, paymentPayloadUtils, utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import * as programSelectors from '../selectors/programsSelectors';
import { shouldSuppressSystemInitiatedPricing } from '../selectors/pendingDealerReviewSelectors';
import {
    advanceRetainedProgram,
    buildIncentivesContextId,
    buildProgramContextId,
    buildRetainedProgram,
    describesSameLenderProgram
} from '../utils/programUtils';
import { filterActiveIncentives } from '../utils/incentivesUtil';
import { isMileageFieldsApply } from '../programInvalidatingActions';

const doesPendingApplyMatchResponse = (
    pendingApplyProgram: PendingApplyProgram | undefined,
    action: AnyFSA,
    isProgramIncentivesRestorationEnabled: boolean
): boolean => {
    if (!pendingApplyProgram) return false;

    if (isProgramIncentivesRestorationEnabled) {
        return (
            pendingApplyProgram.offerType === action.meta?.currentOfferType &&
            pendingApplyProgram.term === action.meta?.term &&
            pendingApplyProgram.lenderId === action.meta?.lenderId
        );
    }

    return (
        pendingApplyProgram.requestId === action.meta?.requestId &&
        pendingApplyProgram.programContextId === action.meta?.programContextId &&
        pendingApplyProgram.offerType === action.meta?.currentOfferType &&
        pendingApplyProgram.term === action.meta?.term &&
        pendingApplyProgram.lenderId === action.meta?.lenderId
    );
};

const isAppliedProgramPayment = (action: AnyFSA): boolean => {
    const originalAction = action.meta?.originalAction;
    if (originalAction?.type !== 'APPLIED_PROGRAM') return false;
    const paymentGrid = originalAction.meta?.paymentGrid;
    if (!paymentGrid) return true;
    return Boolean(paymentGrid.isMainPayment) || !paymentGrid.gridAction;
};

const isGridSubPayment = (action: AnyFSA): boolean => {
    const paymentGrid = action.meta?.originalAction?.meta?.paymentGrid;
    return Boolean(paymentGrid?.gridAction) && !paymentGrid?.isMainPayment;
};

const isAppliedProgramGridSubPayment = (action: AnyFSA): boolean =>
    isGridSubPayment(action) && action.meta?.originalAction?.type === 'APPLIED_PROGRAM';

const hasRawCatalogEntries = (raw: Partial<ProgramIncentives> | undefined): boolean =>
    (raw?.conditional?.length ?? 0) + (raw?.nonConditional?.length ?? 0) > 0;

const shouldRehydrateTermCatalogFromPayment = (action: AnyFSA, state: StateTree, paymentOfferType: OfferType): boolean =>
    hasRawCatalogEntries(action.meta?.rawProgramIncentives) &&
    !action.meta?.isStalePaymentResponse &&
    !isAppliedProgramPayment(action) &&
    !isGridSubPayment(action) &&
    offerReduxUtils.incentives.isGridCashDownCatalogCarryEnabled(state) &&
    offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(state) &&
    paymentOfferType !== CASH &&
    paymentOfferType === offerReduxSelectors.getCurrentOfferType(state) &&
    !programSelectors.getPendingApplyProgram(state) &&
    programSelectors.getSelectedTermIncentivesContext(state)?.programContextId == null;

const isNonPrimaryTierResponse = (action: AnyFSA): boolean => action.meta?.requestedTier != null && action.meta?.primary !== true;

const isReapplyForDeferredStandardsPending = (store: DashStore): boolean => {
    const state = store.getState();
    const pending = programSelectors.getPendingApplyProgram(state);
    if (!pending || !offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(state)) return false;
    if (pending.deferredStandardsReapplied) return false;
    const deferredStandardIds = pending.explicitNonConditionalIds ?? [];
    if (!deferredStandardIds.length) return false;
    const appliedNow = new Set([
        ...offerReduxSelectors.getAppliedIncentiveIdsForSelectedTerm(state, pending.offerType),
        ...offerReduxSelectors.getSelectedNonConditionalIdsFromPenciledInputs(state, pending.offerType)
    ]);
    return !deferredStandardIds.every((id) => appliedNow.has(id));
};

const toTermAvailableIncentive = (incentive: Incentive) => ({
    id: incentive.incentiveId,
    amount: incentive.cash,
    exclusions: incentive.exclusions ?? [],
    name: incentive.programName,
    program: incentive.programId,
    type: incentive.incentiveType,
    isStandardIncentive: incentive.isStandardIncentive,
    description: incentive.description
});

const buildTermIncentivesFromResponse = (
    incentives: Partial<ProgramIncentives> | undefined,
    offerType: string,
    months: number | undefined
) => {
    const determined = (list: Incentive[] | undefined) =>
        filterActiveIncentives(
            programSelectors.getIncentivesWithDeterminedAmountsByTerm(list ?? [], months as number),
            offerType as OfferType
        ).map(toTermAvailableIncentive);
    return {
        conditionals: determined(incentives?.conditional),
        nonConditionals: determined(incentives?.nonConditional)
    };
};

const hasCompatibleIds = (ids: number[] | undefined): ids is number[] => Array.isArray(ids) && ids.length > 0;

const resolveCompatibleIncentiveIds = (
    store: DashStore,
    programContextId: string | undefined,
    explicitCompatibleIncentiveIds: number[] | undefined
): number[] | undefined => {
    if (hasCompatibleIds(explicitCompatibleIncentiveIds)) return explicitCompatibleIncentiveIds;
    if (programContextId == null) return undefined;
    const match = programSelectors.getActivePrograms(store.getState()).find((program) => {
        const incentivesContextId = buildIncentivesContextId(program as unknown as ProgramQuote);
        return (
            incentivesContextId != null &&
            programContextId.startsWith(`${incentivesContextId}|`) &&
            hasCompatibleIds(program.compatibleIncentiveIds)
        );
    });
    return match?.compatibleIncentiveIds;
};

// The grid quotes are priced with all standards and no conditionals, so their compatibility set
// describes a hypothetical rather than the deal. Prefer what the payment response established and
// only fall back to the grid's while the term has none, so the catalog is still filtered on load.
const resolveTermCompatibleIncentiveIds = (
    store: DashStore,
    incentivesContext: Pick<ProgramIncentivesMeta, 'programContextId' | 'compatibleIncentiveIds'>,
    trustResponseCompatibility: boolean
): number[] | undefined => {
    const fromResponse = () =>
        resolveCompatibleIncentiveIds(store, incentivesContext.programContextId, incentivesContext.compatibleIncentiveIds);
    if (trustResponseCompatibility) return fromResponse();
    const established = programSelectors.getSelectedTermCompatibleIncentiveIds(store.getState());
    return hasCompatibleIds(established) ? established : fromResponse();
};

const getTermStandardIdSet = (state: StateTree): Set<number> =>
    new Set(programSelectors.getNonConditionalIncentivesFromSelectedTerm(state).map((incentive) => incentive.incentiveId));

const getRecordedConditionalKeys = (state: StateTree, offerType: OfferType): string[] =>
    offerReduxSelectors
        .getSelectedIncentivesByOfferType(state, offerType)
        .filter((key: unknown): key is string => typeof key === 'string' && key.startsWith(`conditional_${offerType}_`));

const getRetainedStandardIds = (state: StateTree, standardIdSet: Set<number>): number[] =>
    (offerReduxSelectors.getAppliedIncentives(state) ?? [])
        .filter((incentive: { manual?: boolean }) => incentive.manual === false)
        .map((incentive: { code?: number | string | null }) => offerReduxUtils.incentives.toValidIncentiveCode(incentive.code))
        .filter(offerReduxUtils.incentives.isDefinedIncentiveCode)
        .filter((code: number) => standardIdSet.has(code));

const hydrateStandardsSelectionFromTerm = (store: DashStore, offerType: OfferType): void => {
    const state = store.getState();
    if (!offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(state)) return;
    if (offerReduxSelectors.hasRecordedNonConditionalSelection(state, offerType)) return;
    const standardIdSet = getTermStandardIdSet(state);
    if (standardIdSet.size === 0) return;
    const echoStandardIds = offerReduxSelectors
        .getAppliedIncentiveIdsForSelectedTerm(state, offerType)
        .filter((id: number) => standardIdSet.has(id));
    const appliedStandardIds = [...new Set([...echoStandardIds, ...getRetainedStandardIds(state, standardIdSet)])];
    if (appliedStandardIds.length === 0) return;
    const standardKeys = offerReduxUtils.incentives.buildNonConditionalSelectionKeys(offerType, appliedStandardIds, false);
    store.dispatch(selectedConditionalIncentives([...getRecordedConditionalKeys(state, offerType), ...standardKeys], true, false));
};

const dropConditionalsMissingFromProgram = (
    store: DashStore,
    offerType: OfferType,
    programIncentives: Partial<ProgramIncentives> | undefined
): void => {
    const state = store.getState();
    if (!offerReduxUtils.incentives.isGridIncentiveManagementEnabled(state, offerType)) return;
    if (!Array.isArray(programIncentives?.conditional)) return;
    const offeredIds = new Set(programIncentives.conditional.map((incentive) => Number(incentive.incentiveId)));
    const selectedKeys = offerReduxSelectors
        .getSelectedIncentivesByOfferType(state, offerType)
        .filter((key: unknown): key is string => typeof key === 'string');
    const conditionalPrefix = `conditional_${offerType}_`;
    const carriedKeys = selectedKeys.filter(
        (key) => key.startsWith(conditionalPrefix) && key !== `${conditionalPrefix}${offerReduxUtils.incentives.NONE_SELECTED}`
    );
    const keptKeys = carriedKeys.filter((key) => offeredIds.has(Number(key.slice(conditionalPrefix.length))));
    if (keptKeys.length === carriedKeys.length) return;
    const conditionalKeys =
        keptKeys.length > 0 ? keptKeys : offerReduxUtils.incentives.buildConditionalSelectionKeys(offerType, [], true);
    const otherKeys = selectedKeys.filter((key) => !key.startsWith(conditionalPrefix));
    store.dispatch(selectedConditionalIncentives([...conditionalKeys, ...otherKeys], true, false));
};

const DEAL_INCENTIVE_KEEPING_ACTIONS: ReadonlySet<string> = new Set([
    offerReduxActionTypes.SELECTED_TERM,
    offerReduxActionTypes.SELECTED_ANNUAL_MILES,
    offerReduxActionTypes.UPDATED_PAYMENT_FREQUENCY
]);

const isPricedContextChange = (action: AnyFSA): boolean =>
    (DEAL_INCENTIVE_KEEPING_ACTIONS.has(action.type) &&
        (Boolean(action.meta?.middleware?.payment) || action.meta?.userIntent === CDL_USER_INTENT.PAYMENT_GRID_CELL_SELECTED)) ||
    (isMileageFieldsApply(action) && Boolean(action.meta?.middleware?.payment));

const recordDealIncentivesBeforeContextChange = (store: DashStore, action: AnyFSA): void => {
    if (!isPricedContextChange(action)) return;
    const state = store.getState();
    const offerType = offerReduxSelectors.getCurrentOfferType(state);
    if (!offerReduxUtils.incentives.isGridIncentiveManagementEnabled(state, offerType)) return;
    if (
        offerReduxSelectors.hasRecordedNonConditionalSelection(state, offerType) &&
        offerReduxSelectors.hasRecordedConditionalSelection(state, offerType)
    ) {
        return;
    }
    if (offerReduxSelectors.getAppliedIncentiveIdsForSelectedTerm(state, offerType).length === 0) return;
    const { conditional, nonConditional } = paymentPayloadUtils.getDealIncentiveIdsForRequest(state, offerType);
    store.dispatch(
        selectedConditionalIncentives(
            [
                ...offerReduxUtils.incentives.buildConditionalSelectionKeys(offerType, conditional, true),
                ...offerReduxUtils.incentives.buildNonConditionalSelectionKeys(offerType, nonConditional, true)
            ],
            true,
            false,
            true
        )
    );
};

const syncTermAvailableIncentives = (
    store: DashStore,
    incentives: Partial<ProgramIncentives> | undefined,
    offerType: string,
    months: number | undefined,
    incentivesContext: Pick<ProgramIncentivesMeta, 'programContextId' | 'incentivesInputsKey' | 'compatibleIncentiveIds'>,
    trustResponseCompatibility = true
): void => {
    if (incentives == null) return;
    const built = buildTermIncentivesFromResponse(incentives, offerType, months);
    const compatibleIncentiveIds = resolveTermCompatibleIncentiveIds(store, incentivesContext, trustResponseCompatibility);

    // Cash deals have no term to write into (terms reducer no-ops for CASH), so route through the
    // cash-specific UPDATE_CASH_AVAILABLE_INCENTIVES branch instead - but still resolve compatibility
    // the same way finance/lease do below, so the cash pick-list is filtered by what the orchestrator
    // reported as compatible (this PR's stated purpose). Program-loss pruning and standards hydration
    // below are term/program concepts that don't apply to cash and stay finance/lease-only.
    if (offerType === CASH && offerReduxUtils.incentives.isCashDealIncentivesFixesEnabled(store.getState())) {
        store.dispatch(
            updateCashAvailableIncentives({
                conditionals: built.conditionals,
                nonConditionals: built.nonConditionals,
                incentivesContext: { compatibleIncentiveIds }
            })
        );
        return;
    }

    store.dispatch(
        updateTermAvailableIncentives({
            ...built,
            incentivesContext: { ...incentivesContext, compatibleIncentiveIds },
            standardsEchoIsAuthoritative: true
        })
    );
    const updatedState = store.getState();
    if (programSelectors.getProgramLossDisabledIds(updatedState).length > 0) {
        const availableIds = [
            ...programSelectors.getConditionalIncentivesFromSelectedTerm(updatedState),
            ...programSelectors.getNonConditionalIncentivesFromSelectedTerm(updatedState)
        ].map((incentive) => incentive.incentiveId);
        store.dispatch(pruneProgramLossIncentives(availableIds));
    }
    hydrateStandardsSelectionFromTerm(store, offerType as OfferType);
};

const payloadTermCarriesProgram = (offer: GenericObject | undefined): boolean =>
    Object.values((offer?.terms?.byId ?? {}) as Record<string, GenericObject>).some((term) =>
        Boolean(term?.lenderProgram?.programName)
    );

const needsProgramRepriceOnOpen = (action: AnyFSA, state: StateTree, offerType: OfferType): boolean => {
    // Sales Check-In left this deal un-priced for dealer review, so MV must not price it on its own
    // initiative either. Gated here rather than in paymentMiddleware: that channel cannot tell a
    // system-initiated calculation from a dealer edit, so a check there would block ordinary desking.
    if (shouldSuppressSystemInitiatedPricing(state)) return false;
    if (!offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(state)) return false;
    if (offerReduxSelectors.getCalculationEndpointByOfferType(state, offerType) !== CALC_ENDPOINTS.programQuotes) return false;
    const offer = action.payload?.offer as GenericObject | undefined;
    if (!offer?.payment?.lenderProgram?.programName) return false;
    return !payloadTermCarriesProgram(offer);
};

const VEHICLE_CHANGE_PAYMENT_TRIGGERS: ReadonlySet<string> = new Set([
    offerReduxActionTypes.SWITCH_VEHICLE,
    offerReduxActionTypes.APPLY_RECOMMENDED_VEHICLE_PAYMENT_DETAILS
]);

const isVehicleChangePayment = (action: AnyFSA): boolean => VEHICLE_CHANGE_PAYMENT_TRIGGERS.has(action.meta?.originalAction?.type);

const syncRetainedProgramDisplay = (store: DashStore, state: StateTree, keepLatchedProgram = false): void => {
    const selectedTerm = offerReduxSelectors.getSelectedTerm(state);
    const programName = selectedTerm?.lenderProgram?.programName;
    if (!programName) return;
    const lenderLegalName = offerReduxSelectors.getLenderLegalName(state) || '';
    const isLatchEnabled = featureToggleSelectors.isRetainedProgramLatchEnabled(state);
    const current = isLatchEnabled
        ? programSelectors.getLatchedRetainedProgramDisplay(state)
        : programSelectors.getRetainedProgramDisplay(state);
    const incoming = buildRetainedProgram(selectedTerm);
    const keepsLatched =
        keepLatchedProgram &&
        current?.program != null &&
        !describesSameLenderProgram(current.program.lenderProgram, incoming?.lenderProgram);
    const program = !isLatchEnabled
        ? undefined
        : keepsLatched
        ? current?.program
        : advanceRetainedProgram(current?.program, incoming);
    const nextProgramName = program ? program.lenderProgram.programName : programName;
    const nextLenderLegalName = program ? program.lenderLegalName || lenderLegalName : lenderLegalName;
    if (
        current?.programName === nextProgramName &&
        current?.lenderLegalName === nextLenderLegalName &&
        program === current?.program
    ) {
        return;
    }
    const offerType = offerReduxSelectors.getCurrentOfferType(state);
    store.dispatch(
        setRetainedProgramDisplay({
            programName: nextProgramName,
            lenderLegalName: nextLenderLegalName,
            offerType,
            program
        })
    );
};

const seedRetainedProgramFromDeal = (store: DashStore, state: StateTree): void => {
    if (!featureToggleSelectors.isRetainedProgramLatchEnabled(state)) return;
    const offerType = offerReduxSelectors.getCurrentOfferType(state);
    const seedsOverUserPriced = programSelectors.explicitLenderP1AppliesToOffer(state, offerType);
    const latched = programSelectors.getLatchedRetainedProgramDisplay(state);
    if (latched && !(seedsOverUserPriced && !latched.program)) return;
    const termProgram = offerReduxSelectors.getSelectedTerm(state)?.lenderProgram;
    if (termProgram?.programName && !(seedsOverUserPriced && offerReduxUtils.isUserPricedProgram(termProgram))) return;
    if (offerType !== offerReduxSelectors.getSavedOfferType(offerReduxSelectors.getPenciledInputs(state))) return;
    const program = advanceRetainedProgram(undefined, buildRetainedProgram(offerReduxSelectors.getDealPaymentProgram(state)));
    if (!program) return;
    store.dispatch(
        setRetainedProgramDisplay({
            programName: program.lenderProgram.programName,
            lenderLegalName: program.lenderLegalName || '',
            offerType,
            program
        })
    );
};

const FINANCE_INCENTIVE_TYPE = 'Finance';

// Offenders are always conditionals, so only the conditional half of the recorded selection is
// rewritten here. Rebuilding every recorded id as a conditional key re-filed standards into the
// wrong namespace and left the same id recorded twice.
const deselectProgramLossOffenders = (store: DashStore, offerType: string, offenders: number[]): void => {
    const offenderSet = new Set(offenders);
    const state = store.getState();
    const conditionalIds = offerReduxSelectors.getSelectedConditionalIdsFromPenciledInputs(state, offerType as OfferType);
    const remaining = conditionalIds.filter((id: number) => !offenderSet.has(id));
    if (remaining.length === conditionalIds.length) return;
    const preservedStandardKeys = offerReduxSelectors
        .getSelectedIncentivesByOfferType(state, offerType as OfferType)
        .filter((key: unknown): key is string => typeof key === 'string' && key.startsWith(`nonconditional_${offerType}_`));
    store.dispatch(
        selectedConditionalIncentives([
            ...remaining.map((id: number) => offerReduxUtils.incentives.buildConditionalIncentiveKey(offerType, id)),
            ...preservedStandardKeys
        ])
    );
};

const clearStaleProgramLossIncentives = (store: DashStore): void => {
    if (programSelectors.getProgramLossDisabledIds(store.getState()).length === 0) return;
    store.dispatch(clearProgramLossIncentives());
};

const reconcileProgramLossIncentives = (store: DashStore, prevState: StateTree, action: AnyFSA): void => {
    const offerType = offerReduxSelectors.getCurrentOfferType(prevState);
    const prevTerm = offerReduxSelectors.getSelectedTerm(prevState, offerType);
    if (prevTerm?.id == null) return;

    const responseTerm = action.payload?.terms?.byId?.[prevTerm.id];
    if (responseTerm == null) return;

    if (responseTerm.lenderProgram?.programName) {
        clearStaleProgramLossIncentives(store);
        return;
    }

    if (action.meta?.includeIncentives === false) return;

    if (!prevTerm.lenderProgram?.programName) return;
    const offenders = programSelectors
        .getConditionalIncentivesFromSelectedTerm(prevState)
        .filter((incentive) => incentive.incentiveType === FINANCE_INCENTIVE_TYPE)
        .map((incentive) => incentive.incentiveId);
    if (!offenders.length) return;
    store.dispatch(disableProgramLossIncentives(offenders));
    deselectProgramLossOffenders(store, offerType, offenders);
};

export const programsMiddleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    if ((action.type === REQUEST_PROGRAMS_SUCCESS || action.type === REQUEST_PROGRAMS_FAILURE) && action.meta?.keptLenderLookup) {
        settleKeptLenderLookup(action);
        return;
    }

    const state = store.getState();

    switch (action.type) {
        case 'APPLIED_PROGRAM': {
            const offerType = offerReduxSelectors.getCurrentOfferType(state);

            if (action.meta?.keepGridOpen !== true) {
                store.dispatch(updatePaymentGridToggle(false));
            }

            const shouldForcePq =
                !programSelectors.explicitLenderP1AppliesToOffer(state, offerType) || action.meta?.manualProgramSelect === true;

            if (shouldForcePq) {
                store.dispatch(updateCurrentCalculationEndpoint(CALC_ENDPOINTS.programQuotes, offerType));
            }

            const appliedProgram = action.payload?.programData;
            const programName = appliedProgram?.lenderProgram?.programName;
            if (programName) {
                store.dispatch(
                    setRetainedProgramDisplay({
                        programName,
                        lenderLegalName: appliedProgram?.lenderLegalName || '',
                        offerType,
                        program: featureToggleSelectors.isRetainedProgramLatchEnabled(state)
                            ? buildRetainedProgram(appliedProgram)
                            : undefined
                    })
                );
            }

            break;
        }
    }

    recordDealIncentivesBeforeContextChange(store, action);

    next(action);

    const updatedState = store.getState();

    switch (action.type) {
        case VEHICLE_CHANGE_SUCCESS: {
            if (!offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(state)) {
                const currentCalcEndpoint = offerReduxSelectors.getCalculationEndpointByOfferType(
                    state,
                    offerReduxSelectors.getCurrentOfferType(state)
                );
                if (currentCalcEndpoint === CALC_ENDPOINTS.programQuotes) break;
            } else {
                store.dispatch(clearProgramsForVehicleChange());
            }
            store.dispatch(clearRetainedProgramDisplay());
            break;
        }
        case offerReduxActionTypes.UPDATED_SHOPPER_CONTACT: {
            if (action.meta?.[KEEP_LENDER_META]) {
                store.dispatch(startKeptLenderLookup(action));
            }
            break;
        }
        case PAYMENT_RECEIVED: {
            const keptLender = programSelectors.getKeptLenderToReapply(action, updatedState);
            if (keptLender) {
                store.dispatch(reapplyKeptLenderProgram(keptLender, action.meta.originalAction));
            }
            reconcileProgramLossIncentives(store, state, action);
            syncRetainedProgramDisplay(
                store,
                updatedState,
                programSelectors.explicitLenderP1AppliesToOffer(
                    updatedState,
                    offerReduxSelectors.getCurrentOfferType(updatedState)
                ) && !isVehicleChangePayment(action)
            );
            if (
                programSelectors.getPendingApplyProgram(updatedState) &&
                isAppliedProgramPayment(action) &&
                !isReapplyForDeferredStandardsPending(store)
            ) {
                store.dispatch(clearPendingApplyProgram());
            }
            if (action.meta?.rawProgramIncentives && isAppliedProgramPayment(action)) {
                const paymentOfferType = action.meta?.offerType ?? offerReduxSelectors.getCurrentOfferType(updatedState);
                const paymentTerm = action.payload?.[paymentOfferType]?.selectedTermMonths;
                const appliedProgramCompatibleIds = action.meta?.appliedProgramCompatibleIncentiveIds;
                store.dispatch(
                    setAvailableIncentivesFromPayment(
                        action.meta.rawProgramIncentives,
                        paymentOfferType,
                        paymentTerm,
                        appliedProgramCompatibleIds
                    )
                );
                if (action.meta.originalAction?.meta?.carriesConditionalsOnSwitch && !action.meta?.isStalePaymentResponse) {
                    dropConditionalsMissingFromProgram(store, paymentOfferType, action.meta.rawProgramIncentives);
                }
            } else {
                const paymentOfferType = action.meta?.offerType ?? offerReduxSelectors.getCurrentOfferType(updatedState);
                if (shouldRehydrateTermCatalogFromPayment(action, updatedState, paymentOfferType)) {
                    store.dispatch(
                        setAvailableIncentivesFromPayment(
                            action.meta.rawProgramIncentives,
                            paymentOfferType,
                            action.payload?.[paymentOfferType]?.selectedTermMonths,
                            action.meta?.appliedProgramCompatibleIncentiveIds
                        )
                    );
                }
            }
            break;
        }
        case PAYMENT_FAILURE: {
            if (programSelectors.getPendingApplyProgram(updatedState) && !isAppliedProgramGridSubPayment(action)) {
                store.dispatch(clearPendingApplyProgram());
            }
            break;
        }
        case INIT_SUCCESS: {
            syncRetainedProgramDisplay(store, updatedState);
            seedRetainedProgramFromDeal(store, store.getState());
            const initOfferType = offerReduxSelectors.getCurrentOfferType(updatedState);
            if (needsProgramRepriceOnOpen(action, updatedState, initOfferType)) {
                store.dispatch(
                    updateCurrentCalculationEndpoint(CALC_ENDPOINTS.programQuotes, initOfferType, { withPayment: true })
                );
            }
            break;
        }
        case REQUEST_PROGRAMS_SUCCESS: {
            if (isNonPrimaryTierResponse(action)) break;
            if (programSelectors.isSupersededTierResponse(updatedState, action.meta)) break;

            const responseOfferType = action.meta?.currentOfferType ?? offerReduxSelectors.getCurrentOfferType(updatedState);
            // A single-offer-type program-orchestrator request (e.g. a cash-only getProgramQuotes
            // call) returns incentives at the top level of the response, not nested under the
            // offer type key - fall back to that shape when the nested one isn't present (DE759607/US2076398).
            const responseIncentives =
                action.payload?.[responseOfferType]?.incentives ??
                (offerReduxUtils.incentives.isCashDealIncentivesFixesEnabled(updatedState)
                    ? action.payload?.incentives
                    : undefined);
            syncTermAvailableIncentives(
                store,
                responseIncentives,
                responseOfferType,
                action.meta?.currentSelectedProgram?.months,
                {
                    programContextId: action.meta?.programContextId,
                    incentivesInputsKey: action.meta?.incentivesInputsKey,
                    // Cash has no active-program list to match a programContextId against (the
                    // fallback resolveCompatibleIncentiveIds below relies on for finance/lease) - the
                    // orchestrator reports its compatibility set directly on the response instead, so
                    // read it from there. Scoped to cash: everywhere else in the repo this field is
                    // per-payment (inside payments[]), not per-offer-type, so reading it unconditionally
                    // at this level would apply to finance/lease too based on a shape no other fixture
                    // produces at that level.
                    compatibleIncentiveIds:
                        responseOfferType === CASH ? action.payload?.[responseOfferType]?.compatibleIncentiveIds : undefined
                },
                !offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(updatedState)
            );
            break;
        }
        case PROGRAMS_INCENTIVES_REQUEST_SUCCESS: {
            const incentivesOfferType = action.meta?.currentOfferType ?? offerReduxSelectors.getCurrentOfferType(updatedState);
            syncTermAvailableIncentives(
                store,
                action.payload?.incentives?.[incentivesOfferType],
                incentivesOfferType,
                action.meta?.term,
                {
                    programContextId: action.meta?.programContextId,
                    incentivesInputsKey: action.meta?.incentivesInputsKey,
                    compatibleIncentiveIds: action.meta?.compatibleIncentiveIds
                }
            );
            const pendingApplyProgram = programSelectors.getPendingApplyProgram(updatedState);
            if (!pendingApplyProgram) break;
            const restorationEnabled = offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(updatedState);
            if (doesPendingApplyMatchResponse(pendingApplyProgram, action, restorationEnabled)) {
                if (pendingApplyProgram.deferredStandardsReapplied) {
                    store.dispatch(clearPendingApplyProgram());
                    break;
                }
                const sameOfferType = offerReduxSelectors.getCurrentOfferType(updatedState) === pendingApplyProgram.offerType;
                const contextStillValid = restorationEnabled
                    ? sameOfferType
                    : sameOfferType &&
                      buildProgramContextId(programSelectors.getSelectedProgram(updatedState)) ===
                          pendingApplyProgram.programContextId;
                if (contextStillValid) {
                    store.dispatch(
                        restorationEnabled
                            ? applySelectedProgram(pendingApplyProgram.program, {
                                  explicitConditionalIds: pendingApplyProgram.explicitConditionalIds,
                                  explicitNonConditionalIds: pendingApplyProgram.explicitNonConditionalIds,
                                  deferredStandardsReapplied: true,
                                  standardsSelectionEstablished: pendingApplyProgram.standardsSelectionEstablished,
                                  manualProgramSelect: pendingApplyProgram.manualProgramSelect,
                                  reapplyCurrentProgram: pendingApplyProgram.reapplyCurrentProgram
                              })
                            : applySelectedProgram(pendingApplyProgram.program)
                    );
                } else {
                    store.dispatch(clearPendingApplyProgram());
                }
            }
            break;
        }
        case PROGRAMS_INCENTIVES_REQUEST_FAILURE: {
            const pendingApplyProgram = programSelectors.getPendingApplyProgram(updatedState);
            const restorationEnabled = offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(updatedState);
            if (doesPendingApplyMatchResponse(pendingApplyProgram, action, restorationEnabled)) {
                store.dispatch(clearPendingApplyProgram());
            }
            break;
        }
    }
};
